import PageTitle from 'components/PageTitle';
import ScrollToTop from 'components/ScrollToTop';
import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, Redirect, Route, Switch, useLocation, withRouter } from 'react-router-dom';
import { useObservable } from 'react-use-observable';
import Producer from 'scenes/Producer';
import store from 'store';
import SelectAccount from 'v2/components/Pages/Authentication/SelectAccount';
import BankAccountConfirmation from 'v2/components/Pages/BankAccountConfirmation';
import BankAccountForRefund from 'v2/components/Pages/BankAccountForRefund';
import ChangePaymentMethod from 'v2/components/Pages/ChangePaymentMethod';
import InviteAffiliate from 'v2/components/Pages/InviteAffiliate';
import InviteNewUser from 'v2/components/Pages/InviteNewUser';
import SubscriptionCancel from 'v2/components/Pages/SubscriptionCancel';
import ResolveRecurringDateChange from 'v2/components/Pages/SubscriptionRecurringDate/ResolveChange';
import GlobalLoading from 'v2/components/Shared/GlobalLoading';
import { useQuery } from 'v2/hooks/useQuery';
import authService from 'v2/services/auth';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import Bankslip from './Bankslip';
import Confirmation from './Confirmation';
import Downloads from './Downloads';
import NotFound from './NotFound';
import ProviderValidation from './ProviderValidation';
import ContentAccessConfirmation from '../v2/components/Pages/ContentAccessConfirmation';

const Routes = () => {
  const query = useQuery();

  useEffect(() => {
    const chatBotId = query.get('chatbot');

    if (chatBotId) {
      window.sessionStorage.setItem('chatBotId', chatBotId);
    }
  }, [query]);

  return (
    <Provider store={store}>
      <BrowserRouter>
        <ScrollToTop />
        <PageTitle />
        <Switch>
          <Redirect path='/redirect/:hash?/:page*' to='/' />
          <Redirect path='/register' to='/' />
          <Redirect path='/login' to='/' />
          <Route path='/bankslip/:hash' component={Bankslip} />
          <Route path='/confirmation/coproducer' component={Confirmation} />
          <Route path='/recurring-date/resolve-change' component={ResolveRecurringDateChange} />
          <PrivateRoute path='/connect/validate/:provider'>
            <ProviderValidation />
          </PrivateRoute>
          <Route path='/downloads/:hash' component={Downloads} />
          <Route path='/content-access-confirmation/:hash' component={ContentAccessConfirmation} />
          <Route path='/bankslip/:hash' component={Bankslip} />
          <Route path='/bank-account-confirmation/:uuid' component={BankAccountConfirmation} />
          <Route path='/bank-account-refund' component={BankAccountForRefund} />
          <Route path='/change-payment-method' component={ChangePaymentMethod} />
          <Route path='/cancel-subscription/:id/:token' component={SubscriptionCancel} />
          <PrivateRoute path='/referred/:producerId?'>
            <InviteNewUser />
          </PrivateRoute>
          <PrivateRoute path='/affiliate/invite/:productId/:affiliateId?' canBePublic={true}>
            <InviteAffiliate />
          </PrivateRoute>
          <PrivateRoute path='/select-account'>
            <SelectAccount />
          </PrivateRoute>
          <PrivateRoute path='/'>
            <Producer />
          </PrivateRoute>
          <Route path='*' component={NotFound} />
        </Switch>
      </BrowserRouter>
    </Provider>
  );
};
const PrivateRoute = ({ canBePublic, children, ...rest }: { canBePublic?: boolean; children: React.ReactNode; [key: string]: any }) => {
  const [isAuthenticated] = useObservable(() => authService.isAuthenticated(), []);
  const [hasInit] = useObservable(() => eduzzAccountsService.isInit(), []);
  const [accesses] = useObservable(() => eduzzAccountsService.getAccesses(), []);
  const [isLogged] = useObservable(() => eduzzAccountsService.getIsLogged(), []);
  const [loadingAccount] = useObservable(() => eduzzAccountsService.getLoadingAccount(), []);
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const [permissions] = useObservable(() => eduzzAccountsService.getPermissions(), []);
  const hasSession = localStorage.getItem('ssid');
  const location = useLocation().search;

  return (
    <Route
      {...rest}
      render={() => {
        const query = new URLSearchParams(location);
        const login = Boolean(query.get('login')) ?? false;
        if (login) canBePublic = false;
        if (!canBePublic || (canBePublic && hasSession)) {
          if (!isAuthenticated && !hasInit) {
            const register = Boolean(query.get('register')) ?? false;
            eduzzAccountsService.initialize(register);
          }
        }

        if (canBePublic && !hasInit) return children;
        if (accesses?.length > 1 && !isLogged && !loadingAccount) return <SelectAccount />;

        if (loadingAccount || !isAuthenticated || !user || !permissions || !isLogged) return <GlobalLoading />;
        return children;
      }}
    />
  );
};

export default withRouter(Routes);
